import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { T, Toast } from './theme';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Gothram/Surname typeahead
  const [gothramOptions, setGothramOptions] = useState([]);
  const [surnameOptions, setSurnameOptions] = useState([]);
  const [showGothramDrop, setShowGothramDrop] = useState(false);
  const [showSurnameDrop, setShowSurnameDrop] = useState(false);
  const [gothramValid, setGothramValid] = useState(false);
  const [surnameValid, setSurnameValid] = useState(false);
  const [gothramTouched, setGothramTouched] = useState(false);
  const [lastValidGothram, setLastValidGothram] = useState('');
  const [lastValidSurname, setLastValidSurname] = useState('');
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneTaken, setPhoneTaken] = useState(false);
  const phoneTimer = useRef(null);
  const gothramRef = useRef(null);
  const surnameRef = useRef(null);
  const gothramTimer = useRef(null);
  const surnameTimer = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (gothramRef.current && !gothramRef.current.contains(e.target)) setShowGothramDrop(false);
      if (surnameRef.current && !surnameRef.current.contains(e.target)) setShowSurnameDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset typeahead state when edit mode toggled
  useEffect(() => {
    if (!isEditing) {
      setGothramOptions([]); setSurnameOptions([]);
      setShowGothramDrop(false); setShowSurnameDrop(false);
      setGothramValid(false); setSurnameValid(false);
      setGothramTouched(false);
      setLastValidGothram('');
      setPhoneTaken(false); setPhoneChecking(false);
    } else {
      setGothramValid(true); setSurnameValid(true);
      setGothramTouched(false);
      setLastValidGothram(user.gothram || '');
      setLastValidSurname(user.surname || '');
      setPhoneTaken(false);
    }
  }, [isEditing]);

  const handleGothramChange = (val) => {
    setEditedUser(prev => ({ ...prev, gothram: val }));
    setGothramTouched(true);
    setGothramValid(false); setSurnameValid(false);

    const trimmed = val.trim();

    // Matches original — restore fully without API call
    if (trimmed === (user.gothram || '').trim()) {
      setGothramValid(true); setSurnameValid(true);
      setEditedUser(prev => ({ ...prev, gothram: val, surname: user.surname || '' }));
      setGothramOptions([]); setShowGothramDrop(false);
      return;
    }

    // Matches last valid — restore without API call
    if (trimmed === lastValidGothram.trim() && trimmed !== '') {
      setGothramValid(true); setSurnameValid(true);
      setGothramOptions([]); setShowGothramDrop(false);
      return;
    }

    if (trimmed.length < 3) { setGothramOptions([]); setShowGothramDrop(false); return; }

    clearTimeout(gothramTimer.current);
    gothramTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'search_gothram', query: trimmed })
        });
        const data = await res.json();
        if (res.ok) {
          const opts = data.gothrams || [];
          setGothramOptions(opts);
          setShowGothramDrop(opts.length > 0);
        }
      } catch { /* silent */ }
    }, 300);
  };

  const handleGothramBlur = () => {
    const trimmed = (editedUser.gothram || '').trim();
    if (!gothramValid && gothramOptions.length > 0) {
      const exactMatch = gothramOptions.find(g => g.trim().toLowerCase() === trimmed.toLowerCase());
      if (exactMatch) selectGothram(exactMatch);
    }
  };

  const selectGothram = async (g) => {
    setShowGothramDrop(false);
    setGothramValid(true);
    setGothramTouched(true);
    setLastValidGothram(g);
    setSurnameOptions([]);

    // Check if existing surname is valid under new gothram
    const existingSurname = user.surname || '';
    if (existingSurname) {
      try {
        const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'validate', gothram: g, surname: existingSurname })
        });
        const data = await res.json();
        if (res.ok && data.valid) {
          // Existing surname still valid under new gothram — keep it
          setEditedUser(prev => ({ ...prev, gothram: g, surname: existingSurname }));
          setSurnameValid(true);
        } else {
          // Existing surname not valid — clear it
          setEditedUser(prev => ({ ...prev, gothram: g, surname: '' }));
          setSurnameValid(false); setLastValidSurname('');
        }
      } catch {
        setEditedUser(prev => ({ ...prev, gothram: g, surname: '' }));
        setSurnameValid(false); setLastValidSurname('');
      }
    } else {
      setEditedUser(prev => ({ ...prev, gothram: g, surname: '' }));
      setSurnameValid(false);
    }
  };

  const handleSurnameChange = (val) => {
    setEditedUser(prev => ({ ...prev, surname: val }));
    setSurnameValid(false);
    const trimmed = val.trim();

    if (trimmed === (user.surname || '').trim() && !gothramTouched) {
      setSurnameValid(true); setShowSurnameDrop(false); setSurnameOptions([]);
      return;
    }
    if (trimmed === lastValidSurname.trim() && trimmed !== '') {
      setSurnameValid(true); setShowSurnameDrop(false); setSurnameOptions([]);
      return;
    }

    if (!gothramValid || trimmed.length < 3) { setSurnameOptions([]); setShowSurnameDrop(false); return; }

    clearTimeout(surnameTimer.current);
    surnameTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'search_surname', gothram: editedUser.gothram, query: trimmed })
        });
        const data = await res.json();
        if (res.ok) { setSurnameOptions(data.surnames || []); setShowSurnameDrop((data.surnames || []).length > 0); }
      } catch { /* silent */ }
    }, 300);
  };

  const handleSurnameBlur = () => {
    const trimmed = (editedUser.surname || '').trim();
    if (!surnameValid && surnameOptions.length > 0) {
      const exactMatch = surnameOptions.find(s => s.trim().toLowerCase() === trimmed.toLowerCase());
      if (exactMatch) selectSurname(exactMatch);
    }
  };

  const selectSurname = (s) => {
    setEditedUser(prev => ({ ...prev, surname: s }));
    setSurnameValid(true); setLastValidSurname(s); setShowSurnameDrop(false);
  };

  const handlePhoneChange = (val) => {
    setEditedUser(prev => ({ ...prev, phoneNumber: val }));
    setPhoneTaken(false);
    // Only validate if phone actually changed from original
    if (val.trim() === (user.phoneNumber || '').trim()) { setPhoneChecking(false); return; }
    if (val.trim().length < 10) { setPhoneChecking(false); return; }
    clearTimeout(phoneTimer.current);
    setPhoneChecking(true);
    phoneTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/vrv-users-check-phone`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phoneNumber: val.trim(), excludeEmail: user.emailID })
        });
        const data = await res.json();
        setPhoneTaken(data.exists === true);
      } catch { /* silent */ }
      setPhoneChecking(false);
    }, 500);
  };

  useEffect(() => {
    const emailID = localStorage.getItem('vrvEmail');
    if (!emailID) { setError('Email not found — please log in.'); return; }
    const ctrl = new AbortController();
    fetch(`${API_BASE}/vrv-users-getUserDetails`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailID }),
      signal: ctrl.signal,
    })
      .then(r => { if (!r.ok) throw new Error('Failed to fetch'); return r.json(); })
      .then(data => {
        setUser(data); setEditedUser(data);
        if (data.name) localStorage.setItem('vrvName', data.name);
        if (data.surname) localStorage.setItem('vrvSurname', data.surname);
      })
      .catch(e => { if (e.name !== 'AbortError') setError(e.message); });
    return () => ctrl.abort();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const hasChanges = () => user && editedUser &&
    ['name', 'surname', 'phoneNumber', 'dob', 'gothram', 'address'].some(k => (editedUser[k] || '') !== (user[k] || ''));

  const isValidToSave = () => {
    if (phoneTaken || phoneChecking) return false;
    if (!gothramTouched) return hasChanges();
    return hasChanges() && gothramValid && surnameValid;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE}/vrv-users-update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          emailID: user.emailID, requestorRole: 'USER',
          updates: { name: editedUser.name || '', surname: editedUser.surname || '', phoneNumber: String(editedUser.phoneNumber || ''), dob: editedUser.dob || '', gothram: editedUser.gothram || '', address: editedUser.address || '' },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setUser(editedUser); setIsEditing(false);
        if (editedUser.name) localStorage.setItem('vrvName', editedUser.name);
        if (editedUser.surname) localStorage.setItem('vrvSurname', editedUser.surname);
        showToast('Profile updated!');
        window.dispatchEvent(new Event('vrv-profile-updated'));
      }
      else showToast(data.message || 'Update failed', 'error');
    } catch { showToast('Error connecting to server', 'error'); }
    setSaving(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) { showToast('Passwords do not match', 'error'); return; }
    setPasswordSaving(true);
    try {
      const token = localStorage.getItem('vrvToken');
      const hashText = async (t) => {
        const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(t));
        return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
      };
      const [hC, hN, hCf] = await Promise.all([hashText(passwords.currentPassword), hashText(passwords.newPassword), hashText(passwords.confirmPassword)]);
      const res = await fetch(`${API_BASE}/vrv-auth-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ emailID: user.emailID, currentPassword: hC, newPassword: hN, confirmPassword: hCf }),
      });
      const data = await res.json();
      if (res.ok) { showToast('Password changed!'); setShowPasswordModal(false); setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' }); }
      else showToast(data.message || data.error || 'Failed', 'error');
    } catch { showToast('Error connecting', 'error'); }
    setPasswordSaving(false);
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: T.bg }}>
      <div className="vrv-card p-10 text-center max-w-sm">
        <p className="text-3xl mb-4">⚠️</p>
        <p className="font-semibold" style={{ color: T.danger }}>{error}</p>
      </div>
    </div>
  );

  if (!user) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: T.bg }}>
      <div className="vrv-card p-10 text-center max-w-sm">
        <div className="shimmer w-24 h-24 rounded-full mx-auto mb-4" />
        <div className="shimmer h-5 w-40 mx-auto mb-2 rounded-lg" />
        <div className="shimmer h-3 w-28 mx-auto rounded-lg" />
      </div>
    </div>
  );

  const profileFields = ['name', 'surname', 'phoneNumber', 'dob', 'gothram', 'address'];
  const completeness = user ? Math.round((profileFields.filter(k => user[k]).length / profileFields.length) * 100) : 0;
  const completenessColor = completeness === 100 ? '#22C55E' : completeness >= 60 ? T.gold : T.danger;

  const initials = `${user.name || ''}${user.surname || ''}`.charAt(0).toUpperCase() || user.emailID.charAt(0).toUpperCase();
  const memberSince = user.createdAt ? new Date(user.createdAt).getFullYear() : '—';

  return (
    <div style={{ background: T.bg, color: T.ink, minHeight: '100vh' }}>

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="vrv-hero vrv-hero-no-watermark">
        <div className="vrv-container">
          <div className="relative z-10 flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Avatar */}
            <motion.div initial={{ scale: 0.8, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4 }} className="relative flex-shrink-0">
              <div className="w-28 h-28 rounded-full overflow-hidden"
                style={{ border: `3px solid ${T.gold}`, boxShadow: `0 0 0 6px rgba(212,175,55,0.15), 0 16px 40px rgba(0,0,0,0.3)` }}>
                <img
                  src={photo || `https://www.gravatar.com/avatar/${encodeURIComponent(user.emailID)}?d=mp&f=y`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                />
                {!photo && (
                  <div className="w-full h-full flex items-center justify-center text-3xl font-bold"
                    style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color: T.navyDark, position: 'absolute', top: 0, left: 0 }}>
                    {initials}
                  </div>
                )}
              </div>
              <input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) setPhoto(URL.createObjectURL(f)); }} className="hidden" id="photo-upload" />
              <label htmlFor="photo-upload"
                className="absolute bottom-0 right-0 w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color: T.navyDark, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </label>
            </motion.div>

            {/* Info */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.1 }} className="flex-1 text-center md:text-left">
              <p className="vrv-tag mb-2">Member Profile</p>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {user.name} {user.surname}
              </h1>
              <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Inter, sans-serif' }}>{user.emailID}</p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                <span className="badge badge-gold">{user.role}</span>
                <span className={`badge ${user.status === 'ACTIVE' ? 'badge-success' : 'badge-danger'}`}>{user.status}</span>
                <span className="badge badge-muted">Member since {memberSince}</span>
              </div>
            </motion.div>

            {/* Actions */}
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.2 }} className="flex flex-col gap-2 flex-shrink-0 self-end">
              <div className="flex flex-col items-center" style={{ minWidth: '80px' }}>
                <div className="relative" style={{ width: 64, height: 64 }}>
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke={`url(#gauge-grad)`} strokeWidth="5"
                      strokeLinecap="round" strokeDasharray={`${(completeness / 100) * 175.9} 175.9`}
                      transform="rotate(-90 32 32)" style={{ transition: 'stroke-dasharray 0.8s ease' }} />
                    <defs>
                      <linearGradient id="gauge-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={completenessColor} />
                        <stop offset="100%" stopColor={completeness === 100 ? '#4ADE80' : T.gold} />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold" style={{ color: completenessColor, fontFamily: 'Poppins, sans-serif' }}>{completeness}%</span>
                  </div>
                </div>
                <p className="text-[9px] font-bold uppercase tracking-wider mt-1.5" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Poppins, sans-serif' }}>Complete</p>
              </div>
              <div className="flex gap-2">
                {!isEditing ? (
                  <>
                    <button onClick={() => setIsEditing(true)} className="btn-outline"
                      style={{ color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.2)', padding: '0.25rem 0.75rem', fontSize: '0.65rem', minHeight: 'unset', letterSpacing: '0.1em', fontWeight: 700 }}>
                      Edit Profile
                    </button>
                    <button onClick={() => setShowPasswordModal(true)} className="btn-outline"
                      style={{ color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.2)', padding: '0.25rem 0.75rem', fontSize: '0.65rem', minHeight: 'unset', letterSpacing: '0.1em', fontWeight: 700 }}>
                      Change Password
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setIsEditing(false); setEditedUser(user); }} className="btn-outline"
                      style={{ color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.2)', padding: '0.25rem 0.75rem', fontSize: '0.65rem', minHeight: 'unset', letterSpacing: '0.1em', fontWeight: 700 }}>
                      Cancel
                    </button>
                    {hasChanges() && (
                      <button onClick={handleSave} disabled={saving || !isValidToSave()} className="btn-gold"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.65rem', minHeight: 'unset', letterSpacing: '0.1em', fontWeight: 700, opacity: isValidToSave() ? 1 : 0.45 }}>
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="vrv-container py-10">
        {/* ── Detail Cards ────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Details */}
          <div className="vrv-card">
            <div className="px-8 py-5" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <p className="vrv-tag mb-1">Personal Details</p>
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>My Information</h3>
            </div>
            <div className="px-8 py-6 space-y-5">
              {[
                { label: 'First Name', name: 'name', value: user.name },
              ].map((field) => {
                const isChanged = isEditing && (editedUser[field.name] || '') !== (user[field.name] || '');
                const editBorder = isChanged ? '2px solid #22C55E' : `2px solid ${T.gold}`;
                const editBg = isChanged ? 'rgba(34,197,94,0.04)' : 'white';
                return (
                  <div key={field.name}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="vrv-label" style={{ marginBottom: 0 }}>{field.label}</label>
                      {isChanged && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#22C55E' }}>Modified</span>}
                      {isEditing && !isChanged && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.gold }}>Editable</span>}
                    </div>
                    {isEditing
                      ? <input type="text" name={field.name} value={editedUser[field.name] || ''}
                          onChange={e => setEditedUser({ ...editedUser, [e.target.name]: e.target.value })}
                          className="vrv-input" style={{ border: editBorder, background: editBg, transition: 'border 0.2s, background 0.2s' }} />
                      : <p className="text-sm font-medium py-2.5 px-3 rounded-xl" style={{ background: '#FDFBF7', border: '1px solid rgba(0,0,0,0.06)', color: T.ink }}>{field.value || '—'}</p>}
                  </div>
                );
              })}

              {/* Gothram — typeahead */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="vrv-label" style={{ marginBottom: 0 }}>Gothram</label>
                  {isEditing && gothramTouched && !gothramValid && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#EF4444' }}>Invalid</span>}
                  {isEditing && gothramTouched && gothramValid && (editedUser.gothram || '') !== (user.gothram || '') && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#22C55E' }}>Modified</span>}
                  {isEditing && (!gothramTouched || (gothramTouched && gothramValid && (editedUser.gothram || '') === (user.gothram || ''))) && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.gold }}>Editable</span>}
                </div>
                {isEditing ? (
                  <div ref={gothramRef} className="relative">
                    <input type="text" placeholder="Type at least 3 characters..." autoComplete="off"
                      value={editedUser.gothram || ''}
                      onChange={e => handleGothramChange(e.target.value)}
                      onBlur={handleGothramBlur}
                      className="vrv-input"
                      style={{
                        border: gothramTouched ? (gothramValid ? (editedUser.gothram !== user.gothram ? '2px solid #22C55E' : `2px solid ${T.gold}`) : '2px solid #EF4444') : `2px solid ${T.gold}`,
                        background: gothramTouched && !gothramValid ? 'rgba(239,68,68,0.04)' : gothramTouched && gothramValid && editedUser.gothram !== user.gothram ? 'rgba(34,197,94,0.04)' : 'white',
                        transition: 'border 0.2s, background 0.2s'
                      }} />

                    {showGothramDrop && gothramOptions.length > 0 && (
                      <div className="absolute z-20 left-0 right-0 mt-1 rounded-xl overflow-hidden shadow-lg" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', maxHeight: '160px', overflowY: 'auto' }}>
                        {gothramOptions.map(g => (
                          <button key={g} type="button" onClick={() => selectGothram(g)}
                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-amber-50 transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>{g}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm font-medium py-2.5 px-3 rounded-xl" style={{ background: '#FDFBF7', border: '1px solid rgba(0,0,0,0.06)', color: T.ink }}>{user.gothram || '—'}</p>
                )}
              </div>

              {/* Surname — typeahead, unlocks after gothram selected */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="vrv-label" style={{ marginBottom: 0 }}>Surname</label>
                  {isEditing && surnameValid && (editedUser.surname || '') !== (user.surname || '') && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#22C55E' }}>Modified</span>}
                  {isEditing && gothramTouched && gothramValid && !surnameValid && (editedUser.surname || '') !== (user.surname || '') && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#EF4444' }}>Invalid</span>}
                  {isEditing && gothramTouched && !gothramValid && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.muted }}>Locked</span>}
                  {isEditing && (!gothramTouched || gothramValid) && (editedUser.surname || '') === (user.surname || '') && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.gold }}>Editable</span>}
                </div>
                {isEditing ? (
                  <div ref={surnameRef} className="relative">
                    <input type="text" placeholder={gothramValid ? 'Type at least 3 characters...' : 'Select gothram first'}
                      autoComplete="off" disabled={gothramTouched && !gothramValid}
                      value={editedUser.surname || ''}
                      onChange={e => handleSurnameChange(e.target.value)}
                      onBlur={handleSurnameBlur}
                      className="vrv-input"
                      style={{
                        border: gothramTouched && !gothramValid ? `2px solid ${T.gold}` : (editedUser.surname || '') !== (user.surname || '') ? (surnameValid ? '2px solid #22C55E' : '2px solid #EF4444') : `2px solid ${T.gold}`,
                        background: gothramTouched && !gothramValid ? 'white' : (editedUser.surname || '') !== (user.surname || '') ? (surnameValid ? 'rgba(34,197,94,0.04)' : 'rgba(239,68,68,0.04)') : 'white',
                        opacity: (gothramTouched && !gothramValid) ? 0.5 : 1,
                        transition: 'border 0.2s, background 0.2s'
                      }} />

                    {showSurnameDrop && surnameOptions.length > 0 && (
                      <div className="absolute z-20 left-0 right-0 mt-1 rounded-xl overflow-hidden shadow-lg" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', maxHeight: '160px', overflowY: 'auto' }}>
                        {surnameOptions.map(s => (
                          <button key={s} type="button" onClick={() => selectSurname(s)}
                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-amber-50 transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>{s}</button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm font-medium py-2.5 px-3 rounded-xl" style={{ background: '#FDFBF7', border: '1px solid rgba(0,0,0,0.06)', color: T.ink }}>{user.surname || '—'}</p>
                )}
              </div>

              {/* Phone & DOB */}
              {[
                { label: 'Phone Number', name: 'phoneNumber', value: user.phoneNumber },
                { label: 'Date of Birth', name: 'dob', value: user.dob },
              ].map((field) => {
                const isChanged = isEditing && (editedUser[field.name] || '') !== (user[field.name] || '');
                const isPhone = field.name === 'phoneNumber';
                const phoneBorderColor = isPhone && isChanged ? (phoneTaken ? '#EF4444' : phoneChecking ? T.gold : '#22C55E') : (isChanged ? '#22C55E' : T.gold);
                return (
                  <div key={field.name}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="vrv-label" style={{ marginBottom: 0 }}>{field.label}</label>
                      {isPhone && isChanged && phoneChecking && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.gold }}>Checking...</span>}
                      {isPhone && isChanged && !phoneChecking && phoneTaken && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#EF4444' }}>Already taken</span>}
                      {isPhone && isChanged && !phoneChecking && !phoneTaken && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#22C55E' }}>Modified</span>}
                      {(!isPhone || !isChanged) && isChanged && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#22C55E' }}>Modified</span>}
                      {isEditing && !isChanged && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.gold }}>Editable</span>}
                    </div>
                    {isEditing
                      ? <input type="text" name={field.name} value={editedUser[field.name] || ''}
                          onChange={e => isPhone ? handlePhoneChange(e.target.value) : setEditedUser({ ...editedUser, [e.target.name]: e.target.value })}
                          className="vrv-input"
                          style={{ border: `2px solid ${phoneBorderColor}`, background: isChanged ? (phoneTaken ? 'rgba(239,68,68,0.04)' : 'rgba(34,197,94,0.04)') : 'white', transition: 'border 0.2s, background 0.2s' }} />
                      : <p className="text-sm font-medium py-2.5 px-3 rounded-xl" style={{ background: '#FDFBF7', border: '1px solid rgba(0,0,0,0.06)', color: T.ink }}>{field.value || '—'}</p>}
                  </div>
                );
              })}

              {/* Address */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="vrv-label" style={{ marginBottom: 0 }}>Address</label>
                  {isEditing && (editedUser.address || '') !== (user.address || '') && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#22C55E' }}>Modified</span>}
                  {isEditing && (editedUser.address || '') === (user.address || '') && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: T.gold }}>Editable</span>}
                </div>
                {isEditing
                  ? <textarea name="address" value={editedUser.address || ''} onChange={e => setEditedUser({ ...editedUser, address: e.target.value })} rows="3"
                      className="vrv-input resize-none"
                      style={{
                        border: (editedUser.address || '') !== (user.address || '') ? '2px solid #22C55E' : `2px solid ${T.gold}`,
                        background: (editedUser.address || '') !== (user.address || '') ? 'rgba(34,197,94,0.04)' : 'white',
                        transition: 'border 0.2s, background 0.2s'
                      }} />
                  : <p className="text-sm font-medium py-2.5 px-3 rounded-xl whitespace-pre-wrap" style={{ background: '#FDFBF7', border: '1px solid rgba(0,0,0,0.06)', color: T.ink }}>{user.address || '—'}</p>}
              </div>

            </div>
          </div>

          {/* Account Info */}
          <div className="vrv-card">
            <div className="px-8 py-5" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <p className="vrv-tag mb-1">Account Information</p>
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>Account Details</h3>
            </div>
            <div className="px-8 py-6 space-y-5">
              {[
                { label: 'Email ID', value: user.emailID },
                { label: 'Role', value: user.role },
                { label: 'Status', value: user.status },
                { label: 'Last Login', value: user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'N/A' },
                { label: 'Account Created', value: user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A' },
                { label: 'Last Updated', value: user.updatedAt ? new Date(user.updatedAt).toLocaleString() : 'N/A' },
              ].map((field) => (
                <div key={field.label}>
                  <label className="vrv-label">{field.label}</label>
                  <p className="text-sm font-medium py-2.5 px-3 rounded-xl" style={{ background: '#FDFBF7', border: '1px solid rgba(0,0,0,0.06)', color: T.ink }}>{field.value || '—'}</p>
                </div>
              ))}
            </div>
          </div>
        </div>


      </div>

      {/* ── Password Modal ───────────────────────────────────────── */}
      {showPasswordModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4"
          style={{ background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)' }}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.2 }}
            className="w-full max-w-md rounded-3xl overflow-hidden" style={{ background: 'white', boxShadow: '0 30px 80px rgba(0,0,0,0.25)' }}>
            <div className="px-8 py-5" style={{ background: `linear-gradient(135deg, ${T.navyDark}, ${T.navy})`, borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
              <p className="vrv-tag mb-1">Security</p>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>Change Password</h2>
            </div>
            <form onSubmit={handlePasswordSubmit} className="px-8 py-6 space-y-4">
              {[{ label: 'Current Password', name: 'currentPassword' }, { label: 'New Password', name: 'newPassword' }, { label: 'Confirm New Password', name: 'confirmPassword' }].map(f => (
                <div key={f.name}>
                  <label className="vrv-label">{f.label}</label>
                  <input type="password" name={f.name} value={passwords[f.name]} onChange={e => setPasswords({ ...passwords, [e.target.name]: e.target.value })}
                    placeholder="••••••••" className="vrv-input" required />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowPasswordModal(false); setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' }); }}
                  className="flex-1 btn-outline">Cancel</button>
                <button type="submit" disabled={passwordSaving || !passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword}
                  className="flex-1 btn-gold">{passwordSaving ? 'Changing…' : 'Change Password →'}</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default ProfilePage;
